import { logError } from '@hotjar/insights-common/lib/logger';
import { getFilterMetadata } from '../../../components/StatelessComponent/FilterChainUtils';
import type { UserAttributeType } from '../../../components/StatelessComponent/SingleFilter/UserAttributeFilter/UserAttributeFilterType';
import { FilterKindEnum } from '../../../domains/global-filters/model/FilterChain';
import { UserAttributeKind } from '../../../domains/user-attributes/model/UserAttribute';
import type { LabelProps } from '../LabelFormatterType';
import { getValueLabel as radioNumberGetValueLabel } from './GenericLabelFormatter/RadioNumberLabelFormatter';
import { getValueLabel as radioTextGetValueLabel } from './GenericLabelFormatter/RadioTextLabelFormatter';
import { getValueLabel as radioBooleanGetValueLabel } from './GenericLabelFormatter/RadioBooleanLabelFormatter';
import { getValueLabel as radioDateGetValueLabel } from './GenericLabelFormatter/RadioDateLabelFormatter';

const showUnknownUserAttributeKind = (never: never, filterName: string): LabelProps => {
  logError(new Error(`Unknown User attribute kind (We shouldn't reach this part): ${never}`));
  return { label1: filterName, label2: '' };
};

export function getLabels(currentValue: UserAttributeType | undefined): LabelProps {
  if (currentValue?.kind == null) {
    const metadata = getFilterMetadata(FilterKindEnum.UserAttribute);
    return { label1: metadata.filterName, label2: '' };
  }
  switch (currentValue?.kind) {
    case UserAttributeKind.Number: {
      return {
        label1: currentValue.name ?? '',
        label2: radioNumberGetValueLabel(currentValue.value),
      };
    }
    case UserAttributeKind.String: {
      return {
        label1: currentValue.name ?? '',
        label2: radioTextGetValueLabel(currentValue.value),
      };
    }
    case UserAttributeKind.Email: {
      return {
        label1: currentValue.name ?? '',
        label2: radioTextGetValueLabel(currentValue.value),
      };
    }
    case UserAttributeKind.Boolean: {
      return {
        label1: currentValue.name ?? '',
        label2: radioBooleanGetValueLabel(currentValue.value),
      };
    }
    case UserAttributeKind.Date: {
      return {
        label1: currentValue.name ?? '',
        label2: radioDateGetValueLabel(currentValue.value),
      };
    }
    case UserAttributeKind.Unknown: {
      // We should not arrive here as we filter out unknown user attributes
      // But to avoid breaking the flow, we return a default value
      return { label1: currentValue.name ?? '', label2: '' };
    }
    default: {
      const metadata = getFilterMetadata(FilterKindEnum.UserAttribute);
      return showUnknownUserAttributeKind(currentValue, metadata.filterName);
    }
  }
}
